import { useQueries } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useParams } from "react-router-dom";

import { ButtonStandard, Text, toast } from "@performyard/py-components";

import Breadcrumbs from "../../../../../components/Breadcrumbs";
import EmployeeSelection from "../../../../../components/EmployeeSelection";

import { rolesKeys, useAssignRoleEmployees, useRole, useRoleEmployees } from "../../../../../api/hooks/defs/roles";
import useNavigate from "../../../../../hooks/useNavigate";
import useUnsavedChanges from "../../../../../hooks/useUnsavedChanges";

import { getRoleEmployees } from "../../../../../api/services/defs/roles";
import {
	addUserToSelectionObject,
	deleteSelectionObject,
	fetchNewSelectionObject,
} from "../../../../../api/services/defs/selection-object";
import { getUserSelectionUsers } from "../../../../../api/services/defs/user-selection";

import { ROLES_LIST_PATH } from "../../CreateEditRole/validationSchema";

// Page size for reading back the selection. The backend puts no upper bound on bulk assignment, so
// anything past one page is fetched rather than dropped — see the page_count loop in handleSubmit.
const SELECTION_LIMIT = 5000;

// One POST carrying every id would blow Fastify's default 1 MiB body limit around ~37k ids, so the
// assignment is submitted in bounded batches. Re-running after a mid-batch failure is safe: the
// endpoint skips employees already in the role.
export const ASSIGN_BATCH_SIZE = 5000;

// GET /v1/roles/:id/employees caps `limit` at 500 (ListRoleEmployeesQuery in
// packages/service-contracts/src/roles/request.ts). Bulk assignment has no such cap, so a role
// can hold more current employees than one page returns — page through so the exclusion list
// below is never silently partial.
const HOLDERS_PAGE_LIMIT = 500;

// Mirrors `ROLE_CUSTOM_FIELD_NAME` in packages/dto/src/dto/role.ts:10. Backend looks up the
// configured Role custom field by this exact name.
const ROLE_CUSTOM_FIELD_NAME = "Role";

// Stable empty exclusion list — fresh array references invalidate downstream memoization
// in EmployeeSelection.
const EMPTY_USER_IDS: string[] = [];

const ROLE_HEADER_OVERRIDES = { [ROLE_CUSTOM_FIELD_NAME]: "Current Role" };

const detailUrl = (roleId: string) => `/administration/roles/${roleId}?tab=employees`;

const AddEmployeesPageContent: React.FC = () => {
	const navigate = useNavigate();
	const { roleId = "" } = useParams<{ roleId: string }>();

	const roleQuery = useRole(roleId);

	const [selectionObjectId, setSelectionObjectId] = useState<string | null>(null);
	const [exclusionSelectionId, setExclusionSelectionId] = useState<string | null>(null);
	const [isInitializing, setIsInitializing] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const { mutateAsync: assignAsync } = useAssignRoleEmployees(roleId);

	const role = roleQuery.data?.role;
	const isArchived = role?.status === "archived";
	// Deep-link guard: the Add Employees entry point is hidden while Role is HRIS-managed.
	const isRoleFieldLocked = !!roleQuery.data?.roleFieldIntegrationConnected;
	const loadError = roleQuery.error;

	// Employees already holding this role, so the picker below can exclude them. Scoped to only
	// this role — employees on other roles stay selectable, since cross-role reassignment is
	// intentional (RoleService.assignEmployees).
	const firstHoldersPage = useRoleEmployees(roleId, { limit: HOLDERS_PAGE_LIMIT, skip: 0 });
	const holdersTotal = firstHoldersPage.data?.totalCount ?? 0;
	const extraHolderPageCount = Math.max(0, Math.ceil(holdersTotal / HOLDERS_PAGE_LIMIT) - 1);

	const extraHolderQueries = useQueries({
		queries: Array.from({ length: extraHolderPageCount }, (_, i) => {
			const params = { limit: HOLDERS_PAGE_LIMIT, skip: (i + 1) * HOLDERS_PAGE_LIMIT };
			return {
				queryKey: rolesKeys.employees(roleId, params),
				queryFn: () => getRoleEmployees(roleId, params),
				enabled: !!roleId,
			};
		}),
	});

	const holdersReady =
		firstHoldersPage.isSuccess &&
		extraHolderQueries.length === extraHolderPageCount &&
		extraHolderQueries.every((query) => query.isSuccess);

	// holdersReady cannot tell a failed page from a slow one, so without this the page would sit
	// as a form that can never become usable.
	const holdersError = firstHoldersPage.isError || extraHolderQueries.some((query) => query.isError);

	const holderPages = [firstHoldersPage.data, ...extraHolderQueries.map((query) => query.data)];
	const holderIds = holdersReady
		? holderPages.flatMap((page) => page?.employees.map((employee) => employee.user_id) ?? [])
		: EMPTY_USER_IDS;
	const holderIdsSerial = JSON.stringify(holderIds);
	// The picker sends its exclusion on every request and the service serializes it into the GET query
	// (user-selection.ts), so a few hundred holders would push the URL past proxy limits and break
	// search and paging. Hand the holders to the server as their own selection object instead and pass
	// only its id. A role with no holders needs no object at all.
	const needsExclusionSelection = holdersReady && holderIds.length > 0;
	const exclusionReady = holdersReady && (holderIds.length === 0 || !!exclusionSelectionId);

	// `useNavigate` returns a fresh function each render — depending on it directly causes effects
	// to re-fire every render. Mirror values into refs and read through them inside one-shot effects.
	const navigateRef = useRef(navigate);
	const roleIdRef = useRef(roleId);
	useEffect(() => {
		navigateRef.current = navigate;
		roleIdRef.current = roleId;
	});

	useEffect(() => {
		if (loadError) {
			toast.error("Role not found");
			navigateRef.current(ROLES_LIST_PATH);
		} else if (isArchived) {
			toast.error("Reopen this role to add employees");
			navigateRef.current(detailUrl(roleIdRef.current));
		} else if (isRoleFieldLocked) {
			toast.error("Role assignments are managed by your HRIS integration");
			navigateRef.current(detailUrl(roleIdRef.current));
		} else if (holdersError) {
			// No toast here: the global QueryCache handler already reported the failed page.
			navigateRef.current(detailUrl(roleIdRef.current));
		}
	}, [loadError, isArchived, isRoleFieldLocked, holdersError]);

	// Selection-object lifecycle: create temp record on mount, soft-delete on unmount. The DELETE
	// flips `deleted: true` on the record so it stops counting against any quotas / queries.
	useEffect(() => {
		let cancelled = false;
		fetchNewSelectionObject()
			.then((result) => {
				if (cancelled) return;
				setSelectionObjectId(result.user_selection_id);
			})
			.catch(() => {
				if (cancelled) return;
				toast.error("Could not start employee selection. Please try again.");
				navigateRef.current(detailUrl(roleIdRef.current));
			})
			.finally(() => {
				if (!cancelled) setIsInitializing(false);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		// Soft-delete the selection object when the user navigates away. Fire-and-forget; backend
		// TTL still catches orphans if this fails.
		return () => {
			if (selectionObjectId) {
				deleteSelectionObject(selectionObjectId).catch((err: unknown) => {
					console.warn("PY-11788 selection-object cleanup failed", err);
				});
			}
		};
	}, [selectionObjectId]);

	useEffect(() => {
		if (!needsExclusionSelection || exclusionSelectionId) return;
		let cancelled = false;
		void (async () => {
			// Held outside the try so the catch can delete a created-but-unregistered object — a
			// populate failure lands there before the id ever reaches exclusionSelectionId's cleanup.
			let createdId: string | null = null;
			try {
				const created = await fetchNewSelectionObject();
				createdId = created.user_selection_id;
				// One POST with every holder id hits the same 1 MiB body limit the assignment loop
				// batches around — populate the exclusion in the same bounded chunks.
				for (let start = 0; start < holderIds.length && !cancelled; start += ASSIGN_BATCH_SIZE) {
					await addUserToSelectionObject(createdId, holderIds.slice(start, start + ASSIGN_BATCH_SIZE));
				}
				if (cancelled) {
					// Unmounted mid-flight: drop the object we just made rather than orphan it.
					void deleteSelectionObject(createdId).catch((err: unknown) => {
						console.warn("PY-13896 exclusion-selection cleanup failed", err);
					});
					return;
				}
				setExclusionSelectionId(createdId);
			} catch {
				if (createdId) {
					void deleteSelectionObject(createdId).catch((err: unknown) => {
						console.warn("PY-13896 exclusion-selection cleanup failed", err);
					});
				}
				if (cancelled) return;
				toast.error("Could not load this role's current employees. Please try again.");
				navigateRef.current(detailUrl(roleIdRef.current));
			}
		})();
		return () => {
			cancelled = true;
		};
		// holderIdsSerial stands in for holderIds: a fresh array every render would re-run this.
	}, [needsExclusionSelection, exclusionSelectionId, holderIdsSerial]);

	useEffect(() => {
		return () => {
			if (exclusionSelectionId) {
				deleteSelectionObject(exclusionSelectionId).catch((err: unknown) => {
					console.warn("PY-13896 exclusion-selection cleanup failed", err);
				});
			}
		};
	}, [exclusionSelectionId]);

	const handleCancel = () => {
		navigate(detailUrl(roleId));
	};

	// Leaving mid-submit unmounts the page, whose cleanup soft-deletes the selection object the
	// submit loop is still paging — later page reads fail and the assignment stops partway with no
	// feedback. Guard navigation for the duration instead.
	const { setIsUnsafe, setIsSafe } = useUnsavedChanges({
		message: "Employees are still being assigned. Leaving now may interrupt the assignment. Leave anyway?",
	});

	const handleSubmit = async () => {
		if (!selectionObjectId || isSubmitting) return;
		setIsSubmitting(true);
		setIsUnsafe();
		let assignedCount = 0;
		let totalCount = 0;
		try {
			const fetchSelectionPage = (page: number) =>
				getUserSelectionUsers({
					selectionObjectID: selectionObjectId,
					limitSelected: true,
					limit: SELECTION_LIMIT,
					page,
					exclusionList: [],
					filters: {},
					sortField: "name",
					sortDirection: "asc",
				});

			const firstPage = await fetchSelectionPage(1);
			const userIds = firstPage.users.map((u) => u._id);
			// A selection larger than one page would otherwise be assigned in part and reported as
			// everyone — page_count covers the whole selection, so read the rest before assigning.
			for (let page = 2; page <= firstPage.page_count; page++) {
				const nextPage = await fetchSelectionPage(page);
				userIds.push(...nextPage.users.map((u) => u._id));
			}
			if (userIds.length === 0) {
				toast.error("Select at least one employee.");
				setIsSafe();
				setIsSubmitting(false);
				return;
			}
			totalCount = userIds.length;
			// AssignEmployeesBody types user_ids as ObjectId[] (post-Zod-transform); we send strings and
			// the backend re-parses them. Cast bridges the type-level gap.
			const failed: { user_id: string; error: string }[] = [];
			for (let start = 0; start < userIds.length; start += ASSIGN_BATCH_SIZE) {
				const batch = userIds.slice(start, start + ASSIGN_BATCH_SIZE);
				const response = await assignAsync({ user_ids: batch } as unknown as Parameters<typeof assignAsync>[0]);
				// A 200 can still carry per-user failures — count what the server confirmed, not what
				// was submitted.
				assignedCount += response.successful.length;
				failed.push(...response.failed);
			}
			if (failed.length > 0) {
				toast.error(
					`${failed.length} employee${failed.length === 1 ? "" : "s"} could not be assigned` +
						` (${assignedCount} assigned). Submit again to retry — assigned employees are skipped.`
				);
				setIsSafe();
				setIsSubmitting(false);
				return;
			}
			// Safe must land before navigate, or the guard prompts on our own redirect.
			flushSync(() => setIsSafe());
			toast.success(`${assignedCount} ${assignedCount === 1 ? "employee" : "employees"} assigned`);
			navigate(detailUrl(roleId));
		} catch (error) {
			const base = error instanceof Error ? error.message : "An error occurred while assigning employees.";
			toast.error(
				assignedCount > 0
					? `${base} — ${assignedCount} of ${totalCount} were assigned. Submit again to finish; already-assigned employees are skipped.`
					: base
			);
			setIsSafe();
			setIsSubmitting(false);
		}
	};

	if (roleQuery.isLoading || loadError || isArchived || isRoleFieldLocked || holdersError || !role) {
		return <div data-testid="add-employees-loading" />;
	}

	// Submit stays disabled until the selection object and the current-holders exclusion list are
	// both ready. The picker itself is gated on the same conditions below — it never mounts
	// against a null selection id or a partial exclusion list.
	const isPickerReady = !isInitializing && !!selectionObjectId && exclusionReady;

	return (
		<div className="flex flex-col gap-4 px-6 py-8">
			<Breadcrumbs
				pages={[
					{ pageID: "breadcrumb-roles", label: "Roles", url: ROLES_LIST_PATH },
					{ pageID: "breadcrumb-role-name", label: role.name, url: detailUrl(roleId) },
					{ pageID: "breadcrumb-add-employees", label: "Add Employees", url: "" },
				]}
				className="mb-4"
			/>
			<div className="w-full text-center">
				<Text size="sub-heading" weight="semi-bold" color="neutral-9">
					{role.name}
				</Text>
				<Text size="paragraph-2" weight="regular" color="neutral-9">
					Select employees to add to this role. Each employee can only be in one role at a time.
				</Text>
			</div>

			{selectionObjectId && exclusionReady && (
				<div className="relative">
					<EmployeeSelection
						selectionObjectID={selectionObjectId}
						customFieldsIdentifiers={[ROLE_CUSTOM_FIELD_NAME, "Group", "Manager"]}
						customFieldHeaderOverrides={ROLE_HEADER_OVERRIDES}
						exclusionList={EMPTY_USER_IDS}
						usersToExcludeSelectionId={exclusionSelectionId}
						filters={{}}
						paginationAlignment="center"
					/>
					{/* Submit reads the selection page by page; a (de)select mid-flight shifts the page
					    boundaries and corrupts the submitted id list, so the picker goes inert. */}
					{isSubmitting && (
						<div data-testid="employee-picker-shield" className="absolute inset-0 z-10 bg-white/50" />
					)}
				</div>
			)}

			<div className="flex justify-end gap-2">
				<div>
					<ButtonStandard
						size="medium"
						variant="outline"
						color="neutral"
						onClick={handleCancel}
						disabled={isSubmitting}
					>
						Cancel
					</ButtonStandard>
				</div>
				<div>
					<ButtonStandard
						size="medium"
						color="primary"
						onClick={handleSubmit}
						disabled={!isPickerReady || isSubmitting}
						data-testid="add-employees-submit"
					>
						{isSubmitting ? "Assigning…" : "Select Employees"}
					</ButtonStandard>
				</div>
			</div>
		</div>
	);
};

// The page's whole selection lifecycle (create on mount, soft-delete on unmount) assumes one
// role per mount. A history jump between two Add Employees URLs is a param-only navigation that
// reuses the instance — role A's staged selection and holder exclusion would ride into role B's
// submit — so the content is keyed to remount per role.
const AddEmployeesPage: React.FC = () => {
	const { roleId = "" } = useParams<{ roleId: string }>();
	return <AddEmployeesPageContent key={roleId} />;
};

export default AddEmployeesPage;
